import React from "react";
import { Card, CardBody, Nav, Button } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import {
    setAdvancedDate,
    setAdvancedLegend,
    setAdvancedTypeParam,
    setSegmentQueryString,
} from "@pages/Statistics/Transactions/store";

import { StatisticsContext } from "@pages/Statistics/context";

import { useResource } from "@hooks/useResource";

import {
    getAdvancedPeriodStringParam,
    getAdvancedSeeMorePeriod,
    getFamiliesFilterQueryString,
    getFormattedCategoriesByPeriod,
    getStatisticsData,
    getTypeParam,
    getUntranslatedString,
} from "@helpers/general";

import ErrorPage from "@components/Common/ErrorPage";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";
import ChartWrapper from "@pages/Statistics/CommonCharts/ChartWrapper";

import "@assets/apex-chart.css";

export default function StatisticsSalesModeCol() {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();

    const {
        period,
        type,
        advancedDate,
        selections,
        setHistogramData,
        setCategoriesData,
        salesModesSelection,
    } = React.useContext(StatisticsContext);

    const [categories, setCategories] = React.useState<string[]>([]);
    const [top10SubFamiliesData, setTop10SubFamiliesData] = React.useState<
        any[]
    >([]);

    const {
        resourceData: statisticsSubFamiliesData,
        error: statisticsSubFamiliesDataError,
    } = useResource(
        process.env.REACT_APP_API_V1_URL +
            `/statistics/sub-families/sales-mode?type=${getTypeParam(
                type
            )}&${getAdvancedPeriodStringParam(period, advancedDate)}${
                selections.designation &&
                selections.designation !== "my_favorites"
                    ? `&selected=${selections.designation}`
                    : ""
            }${
                getFamiliesFilterQueryString(salesModesSelection!) !== ""
                    ? `&sales-mode=${getFamiliesFilterQueryString(
                          salesModesSelection!
                      )}`
                    : ""
            }`
    );

    React.useEffect(() => {
        setCategories(getFormattedCategoriesByPeriod(period, advancedDate));
        setCategoriesData!(
            getFormattedCategoriesByPeriod(period, advancedDate)
        );
    }, [period, advancedDate, setCategoriesData]);

    React.useEffect(() => {
        setHistogramData!(statisticsSubFamiliesData.data);
        setTop10SubFamiliesData(
            getStatisticsData(
                statisticsSubFamiliesData.data,
                categories,
                period,
                advancedDate
            )
        );
    }, [
        statisticsSubFamiliesData,
        categories,
        period,
        advancedDate,
        setHistogramData,
        t,
    ]);

    function handleSeeMoreButtonOnClickEvent() {
        let segmentObject = {
            designation: "temp",
            value: [
                {
                    designation: "subfamilies",
                    value: top10SubFamiliesData.map(
                        (subFamily) => subFamily.name
                    ),
                },
                {
                    designation: "sales_mode",
                    value: salesModesSelection!
                        .filter((element: string) => {
                            return ![t("All sales methods")].includes(
                                t(element)
                            );
                        })
                        .map(getUntranslatedString),
                },
            ],
        };

        setAdvancedLegend(t("Sub-families"));
        setAdvancedTypeParam(type);
        setSegmentQueryString(encodeURI(JSON.stringify(segmentObject)));

        setAdvancedDate(getAdvancedSeeMorePeriod(period, advancedDate));

        history.push("/statistics-advanced", { prevUrl: location.pathname });
    }

    return (
        <Card>
            <CardBody className="card-body__clz card-body__clz-sm">
                <Nav pills className="navtab-bg float-right d-none">
                    <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={handleSeeMoreButtonOnClickEvent}
                    >
                        {t("See more")}{" "}
                        <i className="mdi mdi-arrow-right ml-1"></i>
                    </Button>
                </Nav>
                <CardBody className="pb-0 chart-order chart-order-sm chart-order-md chart-order-lg chart-order-xl">
                    <div className="apex-charts apex-chart__clz">
                        {statisticsSubFamiliesDataError ? (
                            <ErrorPage />
                        ) : (
                            <CustomErrorBoundary>
                                <ChartWrapper
                                    series={top10SubFamiliesData}
                                    categories={categories}
                                />
                            </CustomErrorBoundary>
                        )}
                    </div>
                </CardBody>
            </CardBody>
        </Card>
    );
}
